import { isolateDirection } from "@frontend-shared/lib";
import { formatRunDurationSeconds } from "@frontend-shared/utils";
import { AlertTriangle, Webhook } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";
import type { IWebhookRunResultCardProps } from "./types";

// NOTE: NSAI-3490 — shared card for both WEBHOOK_RUN_SUCCEEDED and _FAILED
// (same data shape and CTA, only icon/copy differ), mirroring
// PresentationThemeExtractionCard. `onViewRuns` reuses the same
// "view automation runs" handler AGENT_AUTO_PAUSED/SELF_REMINDER already use
// — the Agents page's `?runs=` deep-link already resolves against webhooks,
// not just cron jobs.
const WebhookRunResultCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  isSuccess,
  onViewRuns,
}: IWebhookRunResultCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const handleViewRuns = useCallback((): void => {
    onViewRuns(data.webhookUuid, data.projectUuid);
  }, [onViewRuns, data.webhookUuid, data.projectUuid]);

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId={isSuccess ? "webhook_run_succeeded" : "webhook_run_failed"}
      createdAt={createdAt}
      isRead={isRead}
      icon={
        isSuccess ? (
          <Webhook className="h-5 w-5 text-emerald-500" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-red-500" />
        )
      }
      iconBgClassName={isSuccess ? "bg-emerald-500/10" : "bg-red-500/10"}
      ctaLabel={t("view_runs")}
      onCtaClick={handleViewRuns}
      body={
        <div className="space-y-1">
          <p className="font-medium text-foreground">
            {isSuccess
              ? t("type.webhook_run_succeeded.title")
              : t("type.webhook_run_failed.title")}
          </p>
          <p>
            {isSuccess
              ? t("type.webhook_run_succeeded.body", {
                  webhookName: isolateDirection(data.webhookName),
                  duration: formatRunDurationSeconds(data.durationSec),
                })
              : t("type.webhook_run_failed.body", {
                  webhookName: isolateDirection(data.webhookName),
                  duration: formatRunDurationSeconds(data.durationSec),
                })}
          </p>
          {/* NOTE: Only failures carry an error worth surfacing inline — mirrors
              AgentAutoPausedCard's optional last_error line. The name and the
              error are direction-isolated (NSAI-4800) so an RTL sentence keeps
              a Latin name's punctuation inside its quotes. */}
          {!isSuccess && data.error && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="notification-card-webhook_run_failed-last-error"
            >
              {t("type.webhook_run_failed.last_error", {
                error: isolateDirection(data.error),
              })}
            </p>
          )}
        </div>
      }
    />
  );
};

export const WebhookRunResultCard = memo(WebhookRunResultCardComponent);
